import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  AlertCircle,
  CheckCircle2,
  Clock,
  FileMinus2,
  FileWarning,
  Layers,
  Loader2,
  Mail,
  RefreshCw,
  Send,
  ShieldCheck,
  Square,
  User,
  UserX,
  XCircle,
  Zap,
} from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { Cargando } from '@/shared/ui/Cargando';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { notificar } from '@/shared/ui/avisos';
import { useSesion } from '@/shared/sesion/SesionProvider';
import { PanelDeCorreos, Pestania, RepartoPorPunto } from './PanelesDelProcesoSri';
import { TablaDeTandas } from './TablaDeTandas';
import { ColaDeCorreos } from './ColaDeCorreos';
import { NotasEnElProceso } from './NotasEnElProceso';
import { TablaDeAutorizadas } from './TablaDeAutorizadas';
import { TablaDeLaCola } from './TablaDeLaCola';
import { EsperandoAlSri } from './EsperandoAlSri';
import type { Estado, Pestania as PestaniaId, PorSituacion, Tanda } from './tiposDelProcesoSri';

interface Punto {
  id: number;
  codigo: string;
  nombre: string;
  activo: boolean;

  usuarioNombre: string | null;
  uso?: string | null;
  deNotas?: boolean;
}

export function PaginaProcesoSri() {
  const { puede } = useSesion();
  const clienteDeConsultas = useQueryClient();
  const [elegidos, setElegidos] = useState<number[]>([]);
  const [pestania, setPestania] = useState<PestaniaId>('monitor');
  const [subPestania, setSubPestania] = useState<'errores' | 'esperan' | 'procesadas'>(
    'procesadas',
  );
  const [enCurso, setEnCurso] = useState<number | null>(null);

  const estado = useQuery({
    queryKey: ['proceso-sri'],
    queryFn: () => api.get<Estado>('/proceso-sri'),

    refetchInterval: (consulta) =>
      consulta.state.data?.tanda?.estado === 'en_curso' ? 5000 : false,
  });

  const puntos = useQuery({
    queryKey: ['puntos-emision'],
    queryFn: () => api.get<Punto[]>('/puntos-emision'),
    enabled: puede('puntos_emision.ver'),
  });

  const emitir = useMutation({
    mutationFn: (facturaId: number) =>
      api.post<{ numero: string; situacion: string; mensaje: string }>(
        `/facturas/${facturaId}/emitir?puntoEmision=${elegidos[0]}`,
      ),
    onSuccess: (r) => {
      if (r.situacion === 'autorizada') {
        notificar.exito(`${r.numero} · AUTORIZADA`);
      } else {
        notificar.error(`${r.numero} · ${r.mensaje}`);
      }
      refrescar();
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'No se pudo emitir'),
  });

  const consultar = useMutation({
    mutationFn: (facturaId: number) =>
      api.get<{ situacion: string; mensaje: string }>(`/facturas/${facturaId}/autorizacion`),
    onSuccess: (r) => {
      notificar.exito(r.mensaje);
      refrescar();
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'No se pudo consultar'),
  });

  const arrancarTanda = useMutation({
    mutationFn: () => api.post<Tanda>(`/proceso-sri/emision?puntosEmision=${elegidos.join(',')}`),
    onSuccess: (t) => {
      notificar.exito(`TANDA ${t.id} ARRANCADA · ${t.total.toLocaleString('es-EC')} COMPROBANTES`);
      refrescar();
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'No se pudo arrancar'),
  });

  const detenerTanda = useMutation({
    mutationFn: () => api.post<void>('/proceso-sri/emision/detener'),
    onSuccess: () => {
      notificar.exito('LA TANDA SE DETUVO. LO TOMADO Y NO ENVIADO VUELVE A LA COLA.');
      refrescar();
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'No se pudo detener'),
  });

  const reconsultar = useMutation({
    mutationFn: () =>
      api.post<{ pendientes: number; resueltas: number; siguenEsperando: number }>(
        '/proceso-sri/reconsulta',
      ),
    onSuccess: (r) => {
      if (r.pendientes === 0) {
        notificar.exito('NO HAY NINGUNA ESPERANDO RESPUESTA DEL SRI.');
      } else {
        notificar.exito(`${r.resueltas} RESUELTAS · ${r.siguenEsperando} SIGUEN ESPERANDO AL SRI`);
      }
      refrescar();
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'No se pudo consultar'),
  });

  const enviarCorreos = useMutation({
    mutationFn: () =>
      api.post<{ intentados: number; enviados: number; fallidos: number; quedan: number }>(
        '/proceso-sri/correos?cuantos=100',
      ),
    onSuccess: (r) => {
      if (r.intentados === 0) {
        notificar.exito('NO QUEDA NINGÚN COMPROBANTE POR MANDAR.');
      } else {
        notificar.exito(
          `${r.enviados} ENVIADOS · ${r.fallidos} FALLIDOS · QUEDAN ${r.quedan.toLocaleString('es-EC')}`,
        );
      }
      refrescar();
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'No se pudieron enviar'),
  });

  const probarCorreo = useMutation({
    mutationFn: ({ factura, destino }: { factura: number; destino: string }) =>
      api.post<{ enviado: boolean; destino: string }>(
        `/proceso-sri/correos/prueba?factura=${factura}&destino=${encodeURIComponent(destino)}`,
      ),
    onSuccess: (r) => {
      if (r.enviado) {
        notificar.exito(`SALIÓ HACIA ${r.destino.toUpperCase()}. REVISE EL BUZÓN.`);
      } else {
        notificar.error('NO SALIÓ. REVISE LA CUENTA DE CORREO EN CONFIGURACIÓN.');
      }
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'No se pudo enviar'),
  });

  function refrescar() {
    setEnCurso(null);
    void clienteDeConsultas.invalidateQueries({ queryKey: ['proceso-sri'] });
    void clienteDeConsultas.invalidateQueries({ queryKey: ['facturas'] });
  }

  if (estado.isPending) {
    return (
      <>
        <Encabezado />
        <Cargando titulo="Mirando la cola" detalle="Contando lo emitido" alto="h-64" />
      </>
    );
  }

  const e = estado.data;
  const activos = (puntos.data ?? []).filter((p) => p.activo && !p.uso && !p.deNotas);
  const conUso = (puntos.data ?? []).filter((p) => p.activo && p.uso);
  const listoParaEmitir = elegidos.length > 0;
  const tanda = e?.tanda ?? null;
  const corriendo = tanda?.estado === 'en_curso';
  const puedeProcesar = puede('facturacion_electronica.procesar');

  return (
    <>
      <Encabezado />

      {(emitir.isPending || consultar.isPending) && <EsperandoAlSri />}

      <div className="mb-4 flex gap-1 border-b border-armazon-200">
        <Pestania
          activa={pestania === 'monitor'}
          alPulsar={() => setPestania('monitor')}
          icono={Activity}
          rotulo="Monitor"
        />
        <Pestania
          activa={pestania === 'lotes'}
          alPulsar={() => setPestania('lotes')}
          icono={Layers}
          rotulo="Lotes"
          insignia={tanda?.estado === 'en_curso' ? 'en curso' : undefined}
        />
        <Pestania
          activa={pestania === 'notas'}
          alPulsar={() => setPestania('notas')}
          icono={FileMinus2}
          rotulo="Notas de crédito"
        />
        <Pestania
          activa={pestania === 'correos'}
          alPulsar={() => setPestania('correos')}
          icono={Mail}
          rotulo="Correos"
          insignia={
            (e?.porEnviarCorreo ?? 0) > 0
              ? (e?.porEnviarCorreo ?? 0).toLocaleString('es-EC')
              : undefined
          }
        />
      </div>

      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Tarjeta
          icono={Clock}
          rotulo="Por emitir"
          situacion="interna"
          resumen={e?.resumen}
          tono="espera"
        />
        <Tarjeta icono={Send} rotulo="Enviadas" situacion="enviada" resumen={e?.resumen} />
        <Tarjeta
          icono={CheckCircle2}
          rotulo="Autorizadas"
          situacion="autorizada"
          resumen={e?.resumen}
          tono="bien"
        />
        <Tarjeta
          icono={AlertCircle}
          rotulo="Rechazadas"
          situacion="rechazada"
          resumen={e?.resumen}
          tono="mal"
        />
        <Tarjeta
          icono={FileWarning}
          rotulo="Dadas de baja"
          situacion="anulada_ante_sri"
          resumen={e?.resumen}
        />
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-armazon-200 bg-white p-4">
        <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-armazon-600">
          <ShieldCheck className="size-4 text-armazon-400" aria-hidden />
          Emitir por la caja
        </span>

        <div className="flex flex-wrap items-center gap-1.5">
          {activos.map((p) => {
            const elegido = elegidos.includes(p.id);
            const sinResponsable = !p.usuarioNombre;

            return (
              <button
                key={p.id}
                type="button"
                disabled={sinResponsable}
                onClick={() =>
                  setElegidos((antes) =>
                    antes.includes(p.id) ? antes.filter((otro) => otro !== p.id) : [...antes, p.id],
                  )
                }
                aria-pressed={elegido}
                title={
                  sinResponsable
                    ? `${p.codigo} · ${p.nombre} — sin responsable asignado: asígnele uno en Configuración → Puntos de emisión`
                    : `${p.codigo} · ${p.nombre} — ${p.usuarioNombre}`
                }
                className={`inline-flex h-9 items-center gap-1.5 rounded-lg border px-3 text-xs font-semibold uppercase transition ${
                  sinResponsable
                    ? 'cursor-not-allowed border-dashed border-armazon-200 bg-armazon-50 text-armazon-300'
                    : elegido
                      ? 'border-marca-400 bg-marca-50 text-marca-700'
                      : 'border-armazon-200 bg-white text-armazon-500 hover:border-armazon-300'
                }`}
              >
                {p.codigo}
                {sinResponsable ? (
                  <UserX className="size-3" aria-hidden />
                ) : (
                  <User
                    className={`size-3 ${elegido ? 'text-marca-500' : 'text-armazon-300'}`}
                    aria-hidden
                  />
                )}
              </button>
            );
          })}
        </div>

        {conUso.length > 0 && (
          <span className="text-[10px] uppercase leading-tight text-armazon-500">
            Salen solas por su punto:{' '}
            {conUso
              .map(
                (p) =>
                  `${p.uso === 'EMISION' ? 'emisión mensual' : p.uso === 'GENERADORES' ? 'generadores' : 'Art. 8'} → ${p.codigo}`,
              )
              .join(' · ')}
          </span>
        )}

        <div className="ml-auto flex flex-wrap items-center gap-2">
          {puedeProcesar && !corriendo && (
            <button
              type="button"
              onClick={() => arrancarTanda.mutate()}
              disabled={!listoParaEmitir || arrancarTanda.isPending}
              title={listoParaEmitir ? undefined : 'Elija primero por qué cajas sale'}
              className="inline-flex h-9 items-center gap-2 rounded-lg bg-marca-600 px-3 text-[11px] font-bold uppercase tracking-wide text-white transition hover:bg-marca-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {arrancarTanda.isPending ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : (
                <Zap className="size-4" aria-hidden />
              )}
              Emitir todo lo pendiente
            </button>
          )}

          {puedeProcesar && corriendo && (
            <button
              type="button"
              onClick={() => detenerTanda.mutate()}
              disabled={detenerTanda.isPending}
              className="inline-flex h-9 items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 text-[11px] font-bold uppercase tracking-wide text-red-700 transition hover:bg-red-100 disabled:opacity-40"
            >
              <Square className="size-4" aria-hidden />
              Detener
            </button>
          )}

          {puedeProcesar && (e?.porEnviarCorreo ?? 0) > 0 && (
            <button
              type="button"
              onClick={() => enviarCorreos.mutate()}
              disabled={enviarCorreos.isPending}
              title="El comprobante se entrega cuando ya está autorizado, no antes"
              className="inline-flex h-9 items-center gap-2 rounded-lg border border-armazon-200 px-3 text-[11px] font-bold uppercase tracking-wide text-armazon-700 transition hover:bg-armazon-50 disabled:opacity-40"
            >
              {enviarCorreos.isPending ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : (
                <Mail className="size-4" aria-hidden />
              )}
              Enviar {(e?.porEnviarCorreo ?? 0).toLocaleString('es-EC')} por correo
            </button>
          )}

          {puedeProcesar && (
            <button
              type="button"
              onClick={() => reconsultar.mutate()}
              disabled={reconsultar.isPending}
              title="No manda nada: solo vuelve a preguntar por las que ya se enviaron"
              className="inline-flex h-9 items-center gap-2 rounded-lg border border-armazon-200 px-3 text-[11px] font-bold uppercase tracking-wide text-armazon-700 transition hover:bg-armazon-50 disabled:opacity-40"
            >
              {reconsultar.isPending ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : (
                <RefreshCw className="size-4" aria-hidden />
              )}
              Preguntar por las enviadas
            </button>
          )}
        </div>
      </div>

      {pestania === 'lotes' && tanda && <AvanceDeLaTanda tanda={tanda} />}

      {pestania === 'lotes' && <RepartoPorPunto reparto={e?.reparto ?? []} />}

      {pestania === 'lotes' && <TablaDeTandas tandas={e?.historial ?? []} />}

      {pestania === 'notas' && <NotasEnElProceso punto={elegidos[0] ?? null} />}

      {pestania === 'correos' && <ColaDeCorreos avance={e?.envioDeCorreos ?? null} />}

      {pestania === 'correos' && (
        <PanelDeCorreos
          pendientes={e?.porEnviarCorreo ?? 0}
          probando={probarCorreo.isPending}
          hayComprobante={(e?.ultimasAutorizadas ?? []).length > 0}
          alProbar={(destino) => {
            const ultima = (e?.ultimasAutorizadas ?? [])[0];
            if (ultima) {
              probarCorreo.mutate({ factura: ultima.id, destino });
            }
          }}
        />
      )}

      {pestania === 'monitor' && tanda?.estado === 'en_curso' && <AvanceDeLaTanda tanda={tanda} />}

      {pestania === 'monitor' && (
        <div className="mb-4 flex gap-1 border-b border-armazon-200">
          <Pestania
            activa={subPestania === 'errores'}
            alPulsar={() => setSubPestania('errores')}
            icono={AlertCircle}
            rotulo={`Con errores (${(e?.atascadas ?? []).length})`}
          />
          <Pestania
            activa={subPestania === 'esperan'}
            alPulsar={() => setSubPestania('esperan')}
            icono={Clock}
            rotulo={`Esperan turno (${(e?.porEmitir ?? []).length})`}
          />
          <Pestania
            activa={subPestania === 'procesadas'}
            alPulsar={() => setSubPestania('procesadas')}
            icono={Activity}
            rotulo={`Últimas procesadas (${(e?.ultimasAutorizadas ?? []).length})`}
          />
        </div>
      )}

      {pestania === 'monitor' && subPestania === 'procesadas' && (
        <TablaDeAutorizadas
          filas={e?.ultimasAutorizadas ?? []}
          corriendo={tanda?.estado === 'en_curso'}
        />
      )}

      {pestania === 'monitor' && subPestania === 'esperan' && (
        <TablaDeLaCola
          filas={e?.porEmitir ?? []}
          accion={{
            rotulo: 'Emitir',
            icono: Send,
            alPulsar: (id) => {
              setEnCurso(id);
              emitir.mutate(id);
            },
            disponible: listoParaEmitir,
            aviso: 'Elija primero por qué caja sale',
          }}
          enCurso={enCurso}
          iconoVacio={Clock}
          mensajeVacio="No queda nada por emitir"
          entidad="EN COLA"
        />
      )}

      {pestania === 'monitor' && subPestania === 'errores' && (
        <TablaDeLaCola
          filas={e?.atascadas ?? []}
          conMensaje
          accion={{
            rotulo: 'Volver a preguntar al SRI',
            icono: RefreshCw,
            alPulsar: (id) => {
              setEnCurso(id);
              consultar.mutate(id);
            },
            disponible: true,
          }}
          enCurso={enCurso}
          iconoVacio={CheckCircle2}
          mensajeVacio="Nada atascado"
          entidad="ATASCADAS"
        />
      )}
    </>
  );
}

function AvanceDeLaTanda({ tanda }: { tanda: Tanda }) {
  const porcentaje = tanda.total > 0 ? Math.round((tanda.procesadas / tanda.total) * 100) : 0;

  const rotulo: Record<Tanda['estado'], string> = {
    en_curso: 'En curso',
    terminada: 'Terminada',
    detenida: 'Detenida',
    interrumpida: 'Interrumpida',
  };

  const tono: Record<Tanda['estado'], string> = {
    en_curso: 'border-marca-200 bg-marca-50 text-marca-700',
    terminada: 'border-marca-200 bg-marca-50 text-marca-700',
    detenida: 'border-armazon-200 bg-armazon-50 text-armazon-700',
    interrumpida: 'border-amber-200 bg-amber-50 text-amber-700',
  };

  return (
    <div className="mb-4 rounded-2xl border border-armazon-200 bg-white p-4">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <span
          className={`inline-flex items-center gap-2 rounded-lg border px-2 py-1 text-[11px] font-bold uppercase tracking-wide ${tono[tanda.estado]}`}
        >
          {tanda.estado === 'en_curso' && <Loader2 className="size-3.5 animate-spin" aria-hidden />}
          Tanda {tanda.id} · {rotulo[tanda.estado]}
        </span>

        <span className="text-2xl font-black tabular-nums text-marca-700">{porcentaje}%</span>

        <span className="font-mono text-[11px] text-armazon-500">
          {tanda.procesadas.toLocaleString('es-EC')} / {tanda.total.toLocaleString('es-EC')}
        </span>

        {tanda.iniciadoPor && (
          <span className="ml-auto text-[10px] uppercase text-armazon-500">
            Lanzada por {tanda.iniciadoPor}
          </span>
        )}
      </div>

      <div className="mb-4 h-2.5 overflow-hidden rounded-full bg-armazon-100">
        <div
          className="h-full rounded-full bg-marca-500 transition-all duration-1000 ease-out"
          style={{ width: `${porcentaje}%` }}
        />
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Cifra
          icono={CheckCircle2}
          valor={tanda.emitidas}
          rotulo="Autorizadas"
          tono="text-marca-700"
        />
        <Cifra
          icono={XCircle}
          valor={tanda.rechazadas}
          rotulo="Rechazadas"
          tono="text-red-700"
          ayuda="El SRI las miró y dijo que no: hay algo que corregir en cada una."
        />
        <Cifra
          icono={AlertCircle}
          valor={tanda.errores}
          rotulo="Sin respuesta"
          tono="text-amber-700"
          ayuda="El SRI no contestó: red o servicio caído. No hay nada que corregir, se reintenta."
        />
        <Cifra
          icono={Clock}
          valor={
            tanda.minutosRestantes === null
              ? '—'
              : tanda.minutosRestantes >= 60
                ? `${Math.floor(tanda.minutosRestantes / 60)} h ${tanda.minutosRestantes % 60} min`
                : `${tanda.minutosRestantes} min`
          }
          rotulo={`Restante · ${tanda.porSegundo.toFixed(2)}/s`}
          tono="text-armazon-700"
          ayuda="Al ritmo medio que lleva la tanda. Lo que hay que saber es si cabe en el plazo."
        />
      </div>

      {tanda.mensaje && (
        <p className="mt-3 text-[11px] uppercase text-armazon-600">{tanda.mensaje}</p>
      )}
    </div>
  );
}

function Cifra({
  icono: Icono,
  valor,
  rotulo,
  tono,
  ayuda,
}: {
  icono: typeof CheckCircle2;
  valor: number | string;
  rotulo: string;
  tono: string;
  ayuda?: string;
}) {
  return (
    <div
      className="rounded-xl border border-armazon-100 bg-armazon-50/50 p-3 text-center"
      title={ayuda}
    >
      <Icono className={`mx-auto mb-1 size-4 ${tono}`} aria-hidden />
      <p className={`text-xl font-bold tabular-nums ${tono}`}>
        {typeof valor === 'number' ? valor.toLocaleString('es-EC') : valor}
      </p>
      <p className="text-[10px] font-semibold uppercase text-armazon-500">{rotulo}</p>
    </div>
  );
}

function Encabezado() {
  return (
    <EncabezadoDePagina
      icono={ShieldCheck}
      color="indigo"
      titulo="Proceso SRI"
      subtitulo="Qué se ha emitido, qué espera turno y qué se atascó"
    />
  );
}

function Tarjeta({
  icono: Icono,
  rotulo,
  situacion,
  resumen,
  tono,
}: {
  icono: typeof Clock;
  rotulo: string;
  situacion: string;
  resumen: PorSituacion[] | undefined;
  tono?: 'bien' | 'mal' | 'espera';
}) {
  const fila = (resumen ?? []).find((r) => r.situacion === situacion);
  const cuantas = fila?.cuantas ?? 0;

  const color =
    tono === 'bien'
      ? 'text-marca-700'
      : tono === 'mal' && cuantas > 0
        ? 'text-red-700'
        : tono === 'espera' && cuantas > 0
          ? 'text-amber-700'
          : 'text-armazon-700';

  return (
    <div className="rounded-2xl border border-armazon-200 bg-white p-4">
      <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-armazon-500">
        <Icono className="size-3.5" aria-hidden />
        {rotulo}
      </p>
      <p className={`importe mt-1 text-2xl font-bold ${color}`}>
        {cuantas.toLocaleString('es-EC')}
      </p>
      <p className="importe text-[11px] text-armazon-400">${(fila?.monto ?? 0).toFixed(2)}</p>
    </div>
  );
}
